import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { HelpButton } from "@/components/HelpButton";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import Home from "@/pages/Home";
import Auth from "@/pages/Auth";

const page = (loader) => lazy(loader);
const Legal = page(() => import("@/pages/Legal"));
const ResetPassword = page(() => import("@/pages/ResetPassword"));
const ConfirmEmail = page(() => import("@/pages/ConfirmEmail"));
const Dashboard = page(() => import("@/pages/Dashboard"));
const Tickets = page(() => import("@/pages/Tickets"));
const TicketNew = page(() => import("@/pages/TicketNew"));
const TicketDetail = page(() => import("@/pages/TicketDetail"));
const PaymentSuccess = page(() => import("@/pages/Payment"));
const PaymentCancel = page(() => import("@/pages/Payment").then((m) => ({ default: m.PaymentCancel })));
const Payments = page(() => import("@/pages/Payments"));
const Community = page(() => import("@/pages/Community"));
const Fixes = page(() => import("@/pages/Fixes"));
const ThreadNew = page(() => import("@/pages/ThreadNew"));
const ThreadDetail = page(() => import("@/pages/ThreadDetail"));
const Marketplace = page(() => import("@/pages/Marketplace"));
const ListingNew = page(() => import("@/pages/ListingNew"));
const ListingDetail = page(() => import("@/pages/ListingDetail"));
const Widget = page(() => import("@/pages/Widget"));
const Machines = page(() => import("@/pages/Machines"));
const Messages = page(() => import("@/pages/Messages"));
const MessageThread = page(() => import("@/pages/MessageThread"));
const Approvals = page(() => import("@/pages/Approvals"));
const Staff = page(() => import("@/pages/Staff"));
const AuditLog = page(() => import("@/pages/AuditLog"));
const Customers = page(() => import("@/pages/Customers"));
const TicketChooser = page(() => import("@/pages/TicketChooser"));

const PUBLIC = [["/community", Community], ["/fixes", Fixes], ["/community/:id", ThreadDetail], ["/for-sale", Marketplace], ["/for-sale/:id", ListingDetail]];
const PROTECTED = [
  ["/dashboard", Dashboard], ["/tickets", Tickets], ["/tickets/new", TicketChooser], ["/tickets/new/:tier", TicketNew], ["/tickets/:id", TicketDetail],
  ["/payment/success", PaymentSuccess], ["/payment/cancel", PaymentCancel], ["/payments", Payments],
  ["/community/new", ThreadNew], ["/for-sale/new", ListingNew], ["/for-sale/:id/edit", ListingNew],
  ["/machines", Machines], ["/messages", Messages], ["/messages/:id", MessageThread],
];
const ADMIN = [["/approvals", Approvals], ["/customers", Customers]];
const OWNER = [["/staff", Staff], ["/audit", AuditLog], ["/widget", Widget]];

const Fallback = () => <div className="p-8 text-sm text-slate-500" data-testid="route-loading">Loading…</div>;

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/privacy" element={<Legal kind="privacy" />} />
            <Route path="/terms" element={<Legal kind="terms" />} />
            <Route element={<AppShell />}>
              {PUBLIC.map(([path, C]) => <Route key={path} path={path} element={<C />} />)}
              {PROTECTED.map(([path, C]) => <Route key={path} path={path} element={<ProtectedRoute><C /></ProtectedRoute>} />)}
              {ADMIN.map(([path, C]) => <Route key={path} path={path} element={<ProtectedRoute adminOnly><C /></ProtectedRoute>} />)}
              {OWNER.map(([path, C]) => <Route key={path} path={path} element={<ProtectedRoute adminOnly ownerOnly><C /></ProtectedRoute>} />)}
            </Route>
          </Routes>
        </Suspense>
        <HelpButton />
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
